import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import ReceptiveWords from '../games/ReceptiveWords';
import SupervisorScored from '../games/SupervisorScored';
import SequenceTap from '../games/SequenceTap';
import TimerHold from '../games/TimerHold';
import SpeechGate from '../programs/SpeechGate';
import { PLAYERS } from '../games/kinds';
import { loadMedia } from '../media/media';
import { useLearner } from '../learners/LearnerContext';
import { useT } from '../i18n/i18n';
import { type ConceptKind } from '../concepts/kinds';
import { isKnownKind, type ProgramView } from '../programs/types';

export default function PlayPage() {
  const t = useT();
  const { id } = useParams();
  const [search] = useSearchParams();
  const learnerId = search.get('learner') ?? undefined;
  const { token } = useAuth();
  const [program, setProgram] = useState<ProgramView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { learner } = useLearner();

  useEffect(() => { void loadMedia(learnerId ?? learner?.id ?? null); }, [learnerId, learner?.id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await api<ProgramView>(`/programs/${id}`, { token });
        if (!cancelled) setProgram(p);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('Failed to load'));
      }
    })();
    return () => { cancelled = true; };
  }, [id, token]);

  if (error) return <div className="error" role="alert">{error}</div>;
  if (!program || !id) return null;

  return (
    <SpeechGate kind={program.kind} config={program.config ?? {}}>
      <Player program={program} id={id} learnerId={learnerId} />
    </SpeechGate>
  );
}

function Player({ program, id, learnerId }: { program: ProgramView; id: string; learnerId: string | undefined }) {
  const t = useT();

  /* NOT EVERY KIND CAN BE PLAYED YET, AND THE MESSAGE SAYS WHICH RATHER THAN
     "not supported". A kind the client does not recognise at all is a
     different situation from one it knows but cannot yet play — the first
     means this build is older than the program, the second means the
     playground has not been built. Telling them apart is what stops a
     supervisor retrying something that will never work. */
  if (!isKnownKind(program.kind)) {
    return (
      <div className="card">
        <p>{t('This program is a kind of game this version does not know ({kind}).', { kind: program.kind })}</p>
      </div>
    );
  }

  const kind = program.kind as ConceptKind;
  switch (kind) {
    case 'tileMatch':
      return <ReceptiveWords programId={id} learnerId={learnerId} />;
    case 'supervisorScored':
      return <SupervisorScored programId={id} learnerId={learnerId} />;
    case 'sequenceTap':
      return <SequenceTap programId={id} learnerId={learnerId} />;
    case 'timerHold':
      return <TimerHold programId={id} learnerId={learnerId} />;
    default: {
      const Player = PLAYERS[kind];
      return <Player key={id} programId={id} learnerId={learnerId} />;
    }
  }
}
